import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Modal,
  TextField,
  Snackbar,
  IconButton,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  CardActions,
  Fade,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { Add, Delete, Insights, Close, MenuBook, AutoStories, ViewModule } from '@mui/icons-material';
import NavBar from '../components/NavBar';
import JournalTemplates from '../components/JournalTemplates';
import JournalBook from '../components/JournalBook';
import {
  fetchJournalEntries,
  getInsights,
  createJournalEntry,
  deleteJournalEntry,
  fetchTemplates
} from '../api/journal';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';

const JournalPage = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [entryModalOpen, setEntryModalOpen] = useState(false);
  const [readModalOpen, setReadModalOpen] = useState(false);
  const [insightModalOpen, setInsightModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [insight, setInsight] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [newEntry, setNewEntry] = useState({ body: '' });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'book'
  const [page, setPage] = useState(1);
  const pageSize = 6;
  const pageCount = entries.length > 0 ? Math.ceil(entries.length / pageSize) : 1;
  const paginatedEntries = entries.length > 0 ? entries.slice((page - 1) * pageSize, page * pageSize) : [];

  useEffect(() => {
    loadEntries();
    loadTemplates();
  }, []);

  const loadEntries = async () => {
    setLoading(true);
    const data = await fetchJournalEntries();
    setEntries(data);
    setLoading(false);
  };

  const loadTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const data = await fetchTemplates();
      setTemplates(data);
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleOpenNewEntry = () => {
    setTemplateModalOpen(true);
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setTemplateModalOpen(false);
    setEntryModalOpen(true);
    

    
    setNewEntry({
      body: template.text
    });
  };

  const handleSaveEntry = async () => {
    setSaving(true);
    await createJournalEntry({
      ...newEntry,
      template: selectedTemplate.id
    });
    setEntryModalOpen(false);
    setSelectedTemplate(null);
    setSnackbar({ open: true, message: 'Entry saved!' });
    setSaving(false);
    loadEntries();
  };

  const handleInsights = async (entry) => {
    setSelectedEntry(entry);
    setInsight('');
    setInsightModalOpen(true);
    const result = await getInsights(entry);
    entry.insight = result;
    setInsight(result);
  };

  const handleDelete = (entry) => {
    setSelectedEntry(entry);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    await deleteJournalEntry(selectedEntry.id);
    setDeleteDialogOpen(false);
    setSnackbar({ open: false, message: '' });
    setDeleting(false);
    loadEntries();
  };

  const handleCloseSnackbar = () => setSnackbar({ open: false, message: '' });

  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  const handleReadMore = (entry) => {
    setSelectedEntry(entry);
    setReadModalOpen(true);
  };

  // Responsive FAB for mobile
  const isMobile = window.innerWidth < 600;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: theme.palette.background.default,
        py: 0
      }}
    >
      <NavBar user={user} />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Typography variant="h4" fontWeight={700} sx={{ fontFamily: 'Playfair Display, serif', letterSpacing: 1 }}>
            Your Journal
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              aria-label="view mode"
              size="small"
              sx={{
                bgcolor: 'rgba(255,255,255,0.9)',
                '& .MuiToggleButton-root': {
                  border: 'none',
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    }
                  }
                }
              }}
            >
              <ToggleButton value="cards" aria-label="card view">
                <ViewModule sx={{ mr: 1 }} />
                Cards
              </ToggleButton>
              <ToggleButton value="book" aria-label="book view">
                <AutoStories sx={{ mr: 1 }} />
                Book
              </ToggleButton>
            </ToggleButtonGroup>
            {!isMobile && (
              <Button variant="contained" startIcon={<Add />} onClick={handleOpenNewEntry} sx={{ borderRadius: 999, px: 3, fontWeight: 600 }}>
                New Entry
              </Button>
            )}
          </Box>
        </Box>

        {loading ? (
          <Typography>Loading...</Typography>
        ) : entries.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="50vh"
            sx={{
              bgcolor: 'rgba(255,255,255,0.7)',
              borderRadius: 4,
              boxShadow: '0 4px 24px 0 rgba(80, 120, 180, 0.08)',
              p: { xs: 3, sm: 6 },
              mt: 6
            }}
          >
            <Typography variant="h5" fontWeight={700} sx={{ fontFamily: 'Playfair Display, serif', mb: 2, color: '#457B9D' }}>
              Your journey starts here. Reflect. Write. Heal.
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={4}>
              Begin your first entry and start your path to mindfulness and growth.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<Add />}
              onClick={handleOpenNewEntry}
              sx={{ borderRadius: 999, px: 4, fontWeight: 600, fontSize: '1.1rem', boxShadow: 3 }}
            >
              Write My First Entry
            </Button>
          </Box>
        ) : viewMode === 'book' ? (
          <JournalBook entries={entries} />
        ) : (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 4,
                alignItems: 'stretch',
                mb: 4
              }}
            >
              {paginatedEntries.map((entry) => (
                <Fade in={true} timeout={600} key={entry.id}>
                  <Card
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.3s',
                      position: 'relative',
                      height: '100%',
                      borderRadius: 4,
                      boxShadow: '0 8px 32px 0 rgba(80, 120, 180, 0.10)',
                      border: '2px solid',
                      borderColor: theme.palette.mode === 'dark' ? 'grey.800' : '#b3c6e0',
                      background: theme.palette.background.paper,
                      '&:hover': {
                        boxShadow: '0 16px 48px 0 rgba(80, 120, 180, 0.18)',
                        borderColor: 'primary.main',
                      }
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography
                          variant="h6"
                          fontWeight={700}
                          sx={{ fontFamily: 'Playfair Display, serif', fontSize: '1.25rem', letterSpacing: 0.5, color: 'text.primary' }}
                        >
                          {entry.date}
                        </Typography>
                       
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1, fontSize: '1.05rem', lineHeight: 1.7, minHeight: '2.2em' }}
                      >
                        {entry.text.length > 100 ? entry.text.slice(0, 100) + '…' : entry.text}
                      </Typography>
                    </CardContent>
                    <CardActions sx={{ pt: 0, pb: 2, px: 2 }}>
                      <Button size="small" onClick={() => handleReadMore(entry)} startIcon={<MenuBook />}>
                        Read More
                      </Button>
                      <Button size="small" onClick={() => handleInsights(entry)} startIcon={<Insights />}>
                        Insights
                      </Button>
                      <Button size="small" color="error" onClick={() => handleDelete(entry)} startIcon={<Delete />}>
                        Delete
                      </Button>
                    </CardActions>
                  </Card>
                </Fade>
              ))}
            </Box>
            {/* Pagination Controls */}
            {pageCount > 1 && (
              <Box display="flex" justifyContent="center" alignItems="center" gap={2} mb={2}>
                <Button variant="outlined" onClick={() => setPage(page - 1)} disabled={page === 1} sx={{ borderRadius: 999 }}>
                  Previous
                </Button>
                <Typography variant="body2">Page {page} of {pageCount}</Typography>
                <Button variant="outlined" onClick={() => setPage(page + 1)} disabled={page === pageCount} sx={{ borderRadius: 999 }}>
                  Next
                </Button>
              </Box>
            )}
          </>
        )}

        {/* Mobile FAB */}
        {isMobile && (
          <Fab
            color="primary"
            aria-label="add"
            onClick={handleOpenNewEntry}
            sx={{ position: 'fixed', bottom: 32, right: 32, zIndex: 1000, boxShadow: 6 }}
          >
            <Add />
          </Fab>
        )}

        {/* Template Selection Modal */}
        <Modal open={templateModalOpen} onClose={() => setTemplateModalOpen(false)}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            borderRadius: 4,
            boxShadow: 24,
            p: { xs: 2, sm: 4 },
            width: '90%',
            maxWidth: 800,
            maxHeight: '90vh',
            overflow: 'auto',
            transition: 'all 0.3s',
            animation: 'fadeIn 0.5s'
          }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h6" fontWeight={700} sx={{ fontFamily: 'Playfair Display, serif' }}>
                Choose a Journal Template
              </Typography>
              <IconButton onClick={() => setTemplateModalOpen(false)}>
                <Close />
              </IconButton>
            </Box>
            {templatesLoading ? (
              <Typography>Loading templates...</Typography>
            ) : (
              <JournalTemplates 
                templates={templates} 
                onSelectTemplate={handleSelectTemplate} 
              />
            )}
          </Box>
        </Modal>

        {/* New Entry Modal */}
        <Modal open={entryModalOpen} onClose={() => setEntryModalOpen(false)}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            borderRadius: 4,
            boxShadow: 24,
            p: { xs: 2, sm: 4 },
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            transition: 'all 0.3s',
            animation: 'fadeIn 0.5s'
          }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h5" fontWeight={700} sx={{ fontFamily: 'Playfair Display, serif' }}>
                {selectedTemplate?.title || selectedTemplate?.name}
              </Typography>
              <IconButton onClick={() => setEntryModalOpen(false)}>
                <Close />
              </IconButton>
            </Box>
            <TextField
              fullWidth
              label="Your Entry"
              multiline
              minRows={12}
              maxRows={20}
              value={newEntry.body}
              onChange={e => setNewEntry({ ...newEntry, body: e.target.value })}
              sx={{
                mb: 2,
                '& .MuiInputBase-root': {
                  fontSize: '1.1rem',
                  lineHeight: 1.6,
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.7)',
                  borderRadius: 2,
                  '& .MuiInputBase-input': {
                    color: theme.palette.text.primary
                  }
                }
              }}
            />
            <Box display="flex" gap={2} mt={2}>
              <Button variant="outlined" onClick={() => setEntryModalOpen(false)} sx={{ flex: 1, borderRadius: 999 }}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleSaveEntry} sx={{ flex: 1, borderRadius: 999 }} disabled={saving || !newEntry.body}>
                Save
              </Button>
            </Box>
          </Box>
        </Modal>

        {/* Insights Modal */}
        <Modal open={insightModalOpen} onClose={() => setInsightModalOpen(false)}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            minWidth: 320,
            maxWidth: 500
          }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" fontWeight={600}>
                AI Insights
              </Typography>
              <IconButton onClick={() => setInsightModalOpen(false)}>
                <Close />
              </IconButton>
            </Box>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {insight || 'Analyzing your entry...'}
            </Typography>
          </Box>
        </Modal>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Delete Entry?</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this entry? This action cannot be undone.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={confirmDelete} color="error" disabled={deleting}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Read More Modal */}
        <Modal open={readModalOpen} onClose={() => setReadModalOpen(false)}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h5" fontWeight={600}>
                {selectedEntry?.date}
              </Typography>
              <IconButton onClick={() => setReadModalOpen(false)}>
                <Close />
              </IconButton>
            </Box>
            <Typography variant="body1" sx={{ 
              whiteSpace: 'pre-wrap',
              fontSize: '1.1rem',
              lineHeight: 1.6
            }}>
              {selectedEntry?.text}
            </Typography>
          </Box>
        </Modal>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          message={snackbar.message}
        />
      </Container>
    </Box>
  );
};

export default JournalPage; 